import type { CheckedState } from "@radix-ui/react-checkbox";
import { AlertTriangle, ArrowRight, Check, Info, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { VACANCY_OVERRIDE } from "../constants";
import type { OverrideRow } from "../overrides";
import type { RuleChangeChip } from "../ruleChanges";

import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import { Checkbox } from "@/components/ui/v2/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/v2/dialog";
import { Label } from "@/components/ui/v2/label";
import { useValueFormatter } from "@/utils/useValueFormatter";

type AffectedOverridesDialogProps = {
  segmentName: string;
  affected: OverrideRow[];
  ruleChanges: RuleChangeChip[];
  isSaving: boolean;
  onCancel: () => void;
  onSaveWithoutFixing: () => void;
  onFixAndSave: (vacancyIds: number[]) => void;
};

const getSelectAllState = (
  selectedCount: number,
  total: number,
): CheckedState => {
  if (selectedCount === total) return true;
  if (selectedCount === 0) return false;
  return "indeterminate";
};

export function AffectedOverridesDialog({
  segmentName,
  affected,
  ruleChanges,
  isSaving,
  onCancel,
  onSaveWithoutFixing,
  onFixAndSave,
}: AffectedOverridesDialogProps) {
  const { t } = useTranslation();
  const { format } = useValueFormatter();
  const [selected, setSelected] = useState<ReadonlySet<number>>(
    () => new Set(affected.map((row) => row.vacancy.vacancyId)),
  );

  const toggle = (vacancyId: number) =>
    setSelected((previous) => {
      const next = new Set(previous);
      if (!next.delete(vacancyId)) next.add(vacancyId);
      return next;
    });

  const allSelected = selected.size === affected.length;

  const toggleAll = () =>
    setSelected(
      allSelected
        ? new Set()
        : new Set(affected.map((row) => row.vacancy.vacancyId)),
    );

  return (
    <Dialog open onOpenChange={(next) => !next && onCancel()}>
      <DialogContent
        className="max-h-[90vh] grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto] sm:max-w-2xl"
        data-testid="affected-overrides-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <AlertTriangle
              aria-hidden
              className="size-6 shrink-0 [color:var(--badge-warning-text)]"
            />
            {t("admin.segments.builder.exceptions.dialog.title", {
              count: affected.length,
              formatted: format(affected.length, "whole"),
            })}
          </DialogTitle>
          <DialogDescription>
            {t("admin.segments.builder.exceptions.dialog.description", {
              name: segmentName,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="-mr-2 flex min-h-0 flex-col gap-4 overflow-y-auto pr-2">
          {ruleChanges.length > 0 && (
            <ul className="flex flex-col gap-2 rounded-lg border p-4">
              {ruleChanges.map((change) => (
                <li
                  key={change.key}
                  className="flex flex-wrap items-center gap-2 text-sm"
                >
                  <span className="text-muted-foreground">
                    {change.ruleLabel}
                  </span>
                  {change.before && (
                    <Badge
                      variant="outline"
                      size="large"
                      layout="wrap"
                      className="line-through"
                    >
                      {change.before}
                    </Badge>
                  )}
                  {change.before && change.after && (
                    <ArrowRight
                      aria-hidden
                      className="size-4 shrink-0 text-muted-foreground"
                    />
                  )}
                  {change.after && (
                    <Badge variant="info" size="large" layout="wrap">
                      {change.after}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold">
                {t("admin.segments.builder.exceptions.dialog.affected")}
              </h3>
              <Badge variant="default" size="small" shape="pill">
                {format(affected.length, "whole")}
              </Badge>
              <span className="ml-auto text-xs text-muted-foreground">
                {t("admin.segments.builder.exceptions.dialog.selectedRemoved")}
              </span>
            </div>
            <Label className="flex cursor-pointer items-center gap-3 border border-transparent px-4 text-sm font-normal">
              <Checkbox
                checked={getSelectAllState(selected.size, affected.length)}
                onCheckedChange={toggleAll}
              />
              {t("admin.segments.builder.exceptions.dialog.selectAll")}
            </Label>
            <ul className="flex flex-col gap-2">
              {affected.map((row) => (
                <AffectedRow
                  key={row.vacancy.vacancyId}
                  row={row}
                  checked={selected.has(row.vacancy.vacancyId)}
                  onToggle={() => toggle(row.vacancy.vacancyId)}
                />
              ))}
            </ul>
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Info aria-hidden className="size-4 shrink-0" />
              {t("admin.segments.builder.exceptions.dialog.historyNote")}
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
          >
            <X />
            {t("common.cancel")}
          </Button>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onSaveWithoutFixing}
              disabled={isSaving}
            >
              {t("admin.segments.builder.exceptions.dialog.saveWithoutFixing")}
            </Button>
            <Button
              type="button"
              loading={isSaving}
              disabled={selected.size === 0}
              onClick={() => onFixAndSave([...selected])}
            >
              <Check />
              {allSelected
                ? t("admin.segments.builder.exceptions.dialog.fixAllAndSave")
                : t(
                  "admin.segments.builder.exceptions.dialog.removeSelectedAndSave",
                )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type AffectedRowProps = {
  row: OverrideRow;
  checked: boolean;
  onToggle: () => void;
};

function AffectedRow({ row, checked, onToggle }: AffectedRowProps) {
  const { t } = useTranslation();
  const isExclude = row.type === VACANCY_OVERRIDE.EXCLUDE;
  const typeLabel = isExclude
    ? t("admin.segments.builder.exceptions.manuallyExcluded")
    : t("admin.segments.builder.exceptions.manuallyIncluded");
  const details = [typeLabel, row.vacancy.field, row.vacancy.city]
    .filter(Boolean)
    .join(" · ");

  return (
    <li>
      <Label className="flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 hover-surface-primary">
        <Checkbox checked={checked} onCheckedChange={onToggle} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span
            className="truncate font-medium"
            title={row.vacancy.title ?? undefined}
          >
            {row.vacancy.title ||
              t("admin.segments.builder.vacancies.untitled")}
          </span>
          <span
            className="truncate text-xs font-normal text-muted-foreground"
            title={details}
          >
            {details}
          </span>
        </span>
        <Badge variant="warning">
          {isExclude
            ? t("admin.segments.builder.exceptions.dialog.noLongerInSegment")
            : t("admin.segments.builder.exceptions.dialog.nowMatched")}
        </Badge>
      </Label>
    </li>
  );
}
